<template>
    <div v-if="!pending">
        <div v-show="!isNoData" class="chart chart-wrapper chart-legend-html" style="width:100%; height: 283px;">
            <div id="pieChart" class="chart legend-hover pie-chart" style="width: 200px; height: 100%;"></div>
            <cspReport
                :chartDataList="chartItems"
                :unit="$t('COMMON.UNIT.EA')"
                :colorSet="colorSet.list"
            />
        </div>
        <div v-show="isNoData" class="chart chart-wrapper chart-info-text">
            <p>{{ $t('COMMON.NO_DATA') }}</p>
        </div>
    </div>
</template>

<script>
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import cspReport from "./cspReport";
import _ from "lodash";

export default {
    name: "PieChart",
    components: {
        cspReport,
    },
    props: {
        chartData: Array, // filter 적용된 데이터
        locale: String,
        activeFailStat: String,
        pending: Boolean,
        chartType: String,
    },
    data() {
        return {
            colorSet: {},
            chartItems: [],
            orderBy: 'failedCount',
        }
    },
    watch: {
        chartData(newVal) {
            this.setData(newVal);
        },
        activeFailStat() {
            this.setData(this.chartData);
        }
    },
    computed: {
        isNoData() {
            return this.chartItems.length <= 0;
        }
    },
    updated() {
        const content = document.getElementById("pieChart");
        if (!content || content.childElementCount === 0) {
            // console.log("No pie chart found");
            this.setData(this.chartData);
        } else {
            // console.log("Pie chart mounted");
        }
    },
    methods: {
        setData(chartData) { // create chart
            /*color set*/
            this.colorSet = new am4core.ColorSet();

            if (this.activeFailStat === 'P') {
                // 성공 차트 색상코드
                this.colorSet.list = ["#2F4365", "#1255CB", "#1886FF", "#1EA4FF", "#8CD1FF", "#C6E8FF", "#F3F4F5"].map(function (color) {
                    return new am4core.color(color);
                });
            } else {
                // 실패 차트 색상코드
                this.colorSet.list = ["#CB1D32", "#FF2D47", "#FF7787", "#FFA8BA", "#FFEDF2", "#F3F4F5"].map(function (color) {
                    return new am4core.color(color);
                });
            }
            const data = this.createChartItems(chartData);
            this.chartItems = data.chartItems;
            const ruleList = data.ruleList;
            this.renderChart("pieChart", ruleList, this.chartItems, chartData);
        },
        createChartItems(filterDataList) {
            let self = this;
            // 화면에 표시할 최대 프로덕트 개수
            const CHART_CONTENT_LIMIT_NUM = 6;
            let chartItems = []; //init result list
            // type 에 따른 룰 목록. fail 또는 pass 룰 목록이 됨.
            let ruleList = filterDataList.filter(function (obj) {
                return obj.cmplRslt === self.activeFailStat;
            });
            // type 별 카운트하기
            for (let rule of ruleList) {
                let chartType = rule[this.chartType];
                chartType = typeof (chartType) === 'string' ? JSON.parse(chartType) : chartType;
                if (rule.autoExeYn === "N") {
                    chartType = ["manualRule"];
                }
                for (const type of chartType) {
                    const idx = _.findIndex(chartItems, function (obj) {
                        return obj.product === type
                    });
                    if (idx < 0) { // 데이터 없을 때 신규 chart data 넣기
                        chartItems.push({
                            "name": this.chartType === 'ctgList' ? type : this.$t('PRODUCT.' + type),
                            "product": type,
                            "count": 1,
                            "failedCount": rule.cmplRslt === self.activeFailStat ? 1 : 0,
                            "score": 0
                        });
                    } else {
                        chartItems[idx].count++;
                        if (rule.cmplRslt === self.activeFailStat) {
                            chartItems[idx].failedCount++;
                        }
                    }
                }
            }
            // this.chartItems 정렬
            chartItems = _.reverse(_.sortBy(chartItems, [function (obj) {
                return obj[self.orderBy]
            }]));
            // orderBy 가 0 인 애들 없애주기
            _.remove(chartItems, function (obj) {
                return obj[self.orderBy] === 0;
            });
            // etc 데이터 만들기
            if (chartItems.length > CHART_CONTENT_LIMIT_NUM) {
                let etcData = {
                    "name": this.$t('PRODUCT.etc'),
                    "product": "etc",
                    "count": 0,
                    "failedCount": 0,
                    "score": 0
                };
                for (let i = CHART_CONTENT_LIMIT_NUM - 1; i < chartItems.length; i++) {
                    let chartData = chartItems[i];
                    etcData.count += chartData.count;
                    etcData.failedCount += chartData.failedCount;
                }
                chartItems = chartItems.slice(0, CHART_CONTENT_LIMIT_NUM - 1);
                chartItems.push(etcData);
            }
            // score 세팅
            chartItems.forEach((item) => {
                item.score = (item.failedCount / ruleList.length * 100).toFixed(2) + "%";
            });
            return {ruleList, chartItems};
        },
        renderChart(chartName, ruleList, chartItems, filterDataList) {
            am4core.options.autoSetClassName = true;
            // Themes begin
            am4core.useTheme(am4themes_animated);
            // Themes end

            // Create chart instance
            const chart = am4core.create(chartName, am4charts.PieChart);
            chart.data = chartItems;

            // Add and configure Series
            const pieSeries = chart.series.push(new am4charts.PieSeries());
            pieSeries.dataFields.value = this.orderBy;
            pieSeries.dataFields.category = "name"; // 화면에 표시할 값
            pieSeries.Radius = am4core.percent(5);
            pieSeries.innerRadius = am4core.percent(82);
            pieSeries.slices.template.strokeWidth = 0;
            pieSeries.slices.template.strokeOpacity = 1;
            pieSeries.ticks.template.disabled = true;
            pieSeries.labels.template.disabled = true;

            pieSeries.tooltip.disabled = true;
            pieSeries.colors = this.colorSet;
            pieSeries.slices.template.cursorOverStyle = [
                {
                    "property": "cursor",
                    "value": "pointer"
                }
            ];

            /*list*/
            // TODO :: translate
            pieSeries.legendSettings.valueText = "[#444444]{count}" + this.$t('COMMON.UNIT.EA') + "[/]" + '  ' + "[#999999]({score})[/]";

            chart.legend = new am4charts.Legend();
            chart.legend.position = "right";
            chart.legend.marginRight = 0;
            chart.legend.marginLeft = 10;
            chart.legend.fontSize = 12;
            // chart div교체로 인해 삭제
            //chart.legend.width = am4core.percent(100);
            chart.legend.maxwidth = 600;
            chart.legend.itemContainers.template.togglable = false;

            // chart div교체로 인해 추가
            chart.legend.disabled = true;
            chart.legend.itemContainers.template.adapter.add("paddingLeft", function (event, target) {
                if (target.dataItem.name === "placeholder") {
                    const placehoder = document.querySelectorAll('.pie-chart')[0];
                    target.disabled = true;
                    placehoder.classList.add('placehoder');
                    pieSeries.slices.template.clickable = false;
                }
            });

            /*label marker styling*/
            chart.legend.useDefaultMarker = true;
            let marker = chart.legend.markers.template.children.getIndex(0);
            marker.cornerRadius(0, 0, 0, 0);
            let markerTemplate = chart.legend.markers.template;
            markerTemplate.width = 8;
            markerTemplate.height = 8;
            markerTemplate.marginRight = 5;

            pieSeries.slices.template.events.on("over", function (ev) {
                const index = ev.target.dataItem.index;
                const $chartWrapper = ev.target.parent.htmlContainer.closest('.chart-wrapper');

                const $legendList = $chartWrapper.querySelectorAll('.legend-list')[0];
                const legendListLi = $legendList.children;
                const $legendListLi = Array.prototype.slice.call(legendListLi);

                $legendListLi.forEach(function (el) {
                    el.classList.add('blur')
                })

                if (ev.target.dataItem.properties.category !== 'placeholder') {
                    pieSeries.slices.template.fillOpacity = .2;
                    ev.target.fillOpacity = 1;

                    legendListLi[index].classList.remove('blur');
                    legendListLi[index].classList.add('selected');
                }
            }, this);

            pieSeries.slices.template.events.on("out", function (ev) {
                const $chartWrapper = ev.target.parent.htmlContainer.closest('.chart-wrapper');
                const $legendList = $chartWrapper.querySelectorAll('.legend-list')[0];
                const legendListLi = $legendList.children;
                const $legendListLi = Array.prototype.slice.call(legendListLi);

                $legendListLi.forEach(function (el) {
                    el.classList.remove('blur')
                })
                pieSeries.slices.template.fillOpacity = 1;
            }, this);

            /*pie chart label , 차트 안에 나오는 표현*/
            let langu = this.locale;
            let labelText = 'LABEL.TOTAL_ERROR';
            if (this.activeFailStat === 'P') {
                labelText = 'LABEL.TOTAL_PASSED';
            } else if (this.activeFailStat === 'F') {
                labelText = 'LABEL.TOTAL_FAILED';
            }
            labelText += this.chartType === 'prodList' ? '_PRODUCT' : '_CATEGORY';
            let label = chart.seriesContainer.createChild(am4core.Label);
            label.text = this.$t(labelText);
            label.horizontalCenter = "middle";
            label.verticalCenter = "middle";
            label.textAlign = 'middle';
            label.fontSize = 12;
            label.fill = "#999999";
            if (langu === 'en') {
                label.dy = -28;
            } else {
                label.dy = -20;
            }
            const totalUnit = ruleList.length;
            let label2 = chart.seriesContainer.createChild(am4core.Label);
            label2.text = totalUnit + this.$t('COMMON.UNIT.EA');
            label2.horizontalCenter = "middle";
            label2.verticalCenter = "middle";
            label2.fontSize = 24;
            label2.fontFamily = "Montserrat";
            label2.fill = "#222222";
            label2.fontWeight = "medium";
            label2.dy = 6;

            const label3 = chart.seriesContainer.createChild(am4core.Label);
            const percentResult = (ruleList.length / filterDataList.length * 100).toFixed(2);

            label3.text = (filterDataList.length > 0 ? percentResult : 0) + "%";
            label3.horizontalCenter = "middle";
            label3.verticalCenter = "middle";
            label3.fontSize = 12;
            label3.fontFamily = "Montserrat";
            label3.fill = "#999999";
            label3.dy = 26;
            /*hover*/
            const hs = pieSeries.slices.template.states.getKey("hover");
            hs.properties.scale = 1;
            /*active*/
            pieSeries.slices.template.fillOpacity = 1;

            const as = pieSeries.slices.template.states.getKey("active");
            as.properties.fillOpacity = 1;
        },
    }
}
</script>

<style scoped>
.chart.chart-wrapper.chart-info-text {
    min-height: 238px;
}
</style>



// WEBPACK FOOTER //
// src/components/dashboard/reports/PieChart.vue